import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { divIcon, type LatLngExpression } from "leaflet";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import { Avatar } from "../components/Avatar";
import { Button } from "../components/Button";
import { FormatMascot } from "../components/FormatMascot";
import { Lightbox } from "../components/Lightbox";
import { Loader } from "../components/Loader";
import { TranslatableText } from "../components/TranslatableText";
import { getPublicEvent, setRsvp, type PublicEventDetail } from "../api/events";
import { getMyTicket } from "../api/tickets";
import { getSession } from "../api/client";
import { useAuth } from "../lib/auth-context";
import { formatStartsAt } from "../lib/format";
import type { City, District } from "@nifgash/shared/types";

export function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<PublicEventDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [myStatus, setMyStatus] = useState<"going" | "cancelled" | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [hasTicket, setHasTicket] = useState(false);
  const { auth } = useAuth();
  const myId = auth.kind === "user" ? auth.user.id : null;

  useEffect(() => {
    if (!id) return;
    getPublicEvent(id)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);

  // Authed users: detect whether they already have a check-in ticket (= going),
  // so we can surface the "My ticket" button on load, not just after a toggle.
  useEffect(() => {
    if (!id || !getSession()) return;
    getMyTicket(id).then((tk) => setHasTicket(!!tk)).catch(() => {});
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-[14px] text-brand-red mb-3">{error}</div>
          <Button variant="secondary" onClick={() => navigate("/")}>{t("eventDetail.toHome")}</Button>
        </div>
      </div>
    );
  }
  if (!data) return <Loader text={t("eventDetail.loading")} full />;

  const { event, going_count, attendees } = data;
  const districtText = event.district ? t(`labels.district.${event.district as District}`) : null;
  const cityText = event.city ? t(`labels.city.${event.city as City}`) : null;
  const venueLine = [event.venue_name, event.venue_address, districtText, cityText]
    .filter(Boolean)
    .join(", ");
  const fmt = event.format ? t(`labels.format.${event.format}`, { defaultValue: event.format }) : null;
  const full = event.capacity != null && going_count >= event.capacity && myStatus !== "going";
  const isAuthed = !!getSession();
  const isGoing = myStatus === "going";
  const isHost = myId != null && event.host_user_id === myId;

  async function toggleRsvp() {
    if (!event) return;
    if (!isAuthed) {
      navigate("/login", { state: { from: `/events/${event.id}` } });
      return;
    }
    setRsvpBusy(true);
    try {
      const res = await setRsvp(event.id, !isGoing);
      setMyStatus(res.status);
      setHasTicket(res.status === "going");
      setData((d) => (d ? { ...d, going_count: res.going_count } : d));
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(msg.includes("409") ? t("eventDetail.alertFull") : t("eventDetail.alertGenericError"));
    } finally {
      setRsvpBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface pb-32">
      <header className="px-5 pt-6 pb-4 border-b border-ink-100 sticky top-0 z-10 bg-surface/95 backdrop-blur">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-[13px] text-ink-500 hover:text-ink-900"
        >
          {t("eventDetail.back")}
        </button>
      </header>

      <div className="px-5 pt-6 max-w-[720px] mx-auto">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-14 h-14 rounded-full overflow-hidden">
            <FormatMascot format={event.format} size={56} />
          </div>
          <div className="flex-1 min-w-0">
            <TranslatableText
              as="h1"
              text={event.title}
              className="text-[22px] font-extrabold text-ink-900 leading-tight"
            />
            {fmt && (
              <div className="text-[12px] font-bold uppercase tracking-wider mt-1 text-brand-purple">
                {fmt}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 space-y-2">
          <Row label={t("eventDetail.when")} value={formatStartsAt(event.starts_at)} />
          <TranslatableRow label={t("eventDetail.where")} value={venueLine} />
          <Row
            label={t("eventDetail.going")}
            value={`${going_count}${event.capacity ? ` / ${event.capacity}` : ""}`}
          />
        </div>

        {event.image_url && (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className="block w-full mt-5"
          >
            <img
              src={event.image_url}
              alt=""
              className="w-full max-h-72 object-cover rounded-[var(--radius-md)] border border-ink-100"
            />
          </button>
        )}
        <Lightbox src={lightboxOpen ? event.image_url : null} onClose={() => setLightboxOpen(false)} />

        {event.lat != null && event.lng != null && (
          <div className="mt-5 rounded-[var(--radius-md)] overflow-hidden border border-ink-100" style={{ height: 240 }}>
            <MapContainer
              center={[event.lat, event.lng] as LatLngExpression}
              zoom={15}
              scrollWheelZoom={false}
              style={{ width: "100%", height: "100%" }}
            >
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[event.lat, event.lng]} icon={pinIcon("#7C3AED")} />
            </MapContainer>
          </div>
        )}

        {event.description && (
          <div className="mt-6">
            <TranslatableText
              as="div"
              text={event.description}
              className="text-[15px] text-ink-700 leading-relaxed whitespace-pre-wrap"
            />
          </div>
        )}

        <h3 className="mt-8 text-[13px] font-bold uppercase tracking-wider text-ink-500">
          {t("eventDetail.whoGoing")}
        </h3>
        {attendees.length === 0 ? (
          <p className="mt-2 text-[14px] text-ink-500">{t("eventDetail.nobodyYet")}</p>
        ) : (
          <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-3">
            {attendees.map((a) => (
              <button
                key={a.user_id}
                type="button"
                onClick={() => navigate(`/profile/${a.user_id}`)}
                className="flex flex-col items-center text-center active:scale-95 transition-transform"
              >
                <Avatar url={a.photo_url} name={a.full_name} size={56} />
                <div className="mt-1.5 text-[12px] text-ink-700 truncate max-w-full">
                  {firstWord(a.full_name)}
                </div>
              </button>
            ))}
            {going_count > attendees.length && (
              <div className="flex flex-col items-center justify-center text-[12px] text-ink-500">
                +{going_count - attendees.length}
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 px-5 py-4 bg-surface border-t border-ink-100"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
      >
        <div className="max-w-[720px] mx-auto space-y-2">
          {isHost && (
            <Button stretched size="lg" onClick={() => navigate(`/events/${event.id}/scan`)}>
              {t("eventDetail.scanTickets")}
            </Button>
          )}
          {hasTicket && !isHost && (
            <Button stretched size="lg" onClick={() => navigate(`/events/${event.id}/ticket`)}>
              {t("eventDetail.myTicket")}
            </Button>
          )}
          <Button
            stretched
            size="lg"
            variant={isGoing || hasTicket || isHost ? "secondary" : "primary"}
            disabled={rsvpBusy || full}
            onClick={() => void toggleRsvp()}
          >
            {!isAuthed
              ? t("eventDetail.signInToRsvp")
              : rsvpBusy ? "…"
              : full ? t("eventDetail.full")
              : isGoing ? t("eventDetail.notGoing")
              : t("eventDetail.imGoing")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[12px] font-bold uppercase tracking-wider text-ink-400 w-16 flex-shrink-0">
        {label}
      </span>
      <span className="text-[15px] text-ink-900">{value}</span>
    </div>
  );
}

function TranslatableRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[12px] font-bold uppercase tracking-wider text-ink-400 w-16 flex-shrink-0">
        {label}
      </span>
      <div className="flex-1 min-w-0">
        <TranslatableText as="span" text={value} className="text-[15px] text-ink-900" />
      </div>
    </div>
  );
}

function firstWord(name: string | null): string {
  if (!name) return "";
  return name.split(/\s+/)[0] ?? "";
}

function pinIcon(color: string) {
  const svg = `
    <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 1 C 7 1 1 8 1 16 c 0 11 15 25 15 25 s 15 -14 15 -25 c 0 -8 -6 -15 -15 -15 z"
            fill="${color}" stroke="#2B2F54" stroke-width="2" />
      <circle cx="16" cy="16" r="5" fill="#FFFFFF" />
    </svg>`;
  return divIcon({
    html: svg,
    className: "nf-pin",
    iconSize: [32, 42],
    iconAnchor: [16, 40],
  });
}
